import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, CalendarDays, Truck, MapPin, PackageCheck, ArrowLeft, Phone, Clock, ClipboardCheck, FileText, Share2, Check, XCircle } from 'lucide-react';
import { loadOrderById, decodeOrderToken, buildShareLink, getCurrentStepIndex, getEtaLabel, saveOrderForTracking, cancelOrder, TrackedOrder, STEP_ORDER } from '../utils/orderTracking';
const STEPS = [{
  id: 'confirmed',
  label: 'Order Confirmed',
  icon: CheckCircle2,
  desc: 'We received your order.'
}, {
  id: 'scheduled',
  label: 'Scheduled',
  icon: CalendarDays,
  desc: 'Delivery window confirmed.'
}, {
  id: 'dispatched',
  label: 'Dispatched',
  icon: ClipboardCheck,
  desc: 'Driver assigned and loading.'
}, {
  id: 'en_route',
  label: 'En Route',
  icon: Truck,
  desc: 'Truck is on the way to your site.'
}, {
  id: 'delivered',
  label: 'Delivered',
  icon: PackageCheck,
  desc: 'Material successfully dumped.'
}] as const;
export function TrackOrder() {
  const {
    orderId
  } = useParams<{
    orderId: string;
  }>();
  const location = useLocation();
  const [orderData, setOrderData] = useState<TrackedOrder | null>(null);
  const [now, setNow] = useState<number>(Date.now());
  const [copied, setCopied] = useState(false);
  // Load order: localStorage first, then ?d= URL token fallback
  useEffect(() => {
    window.scrollTo(0, 0);
    if (!orderId) return;
    // 1. localStorage
    const local = loadOrderById(orderId);
    if (local) {
      setOrderData(local);
      return;
    }
    // 2. ?d= URL token (shared tracking link)
    const search = location.search || (location.hash ? '' : '');
    let token: string | null = null;
    if (search) {
      token = new URLSearchParams(search).get('d');
    } else {
      // HashRouter quirk: search params can live inside the hash
      const hash = window.location.hash;
      const qIdx = hash.indexOf('?');
      if (qIdx >= 0) token = new URLSearchParams(hash.slice(qIdx)).get('d');
    }
    if (token) {
      const decoded = decodeOrderToken(token);
      if (decoded && decoded.orderId === orderId) {
        setOrderData(decoded);
        // Persist locally so the customer doesn't need the share link next time
        saveOrderForTracking(decoded);
      }
    }
  }, [orderId, location.search]);
  // Keep status live — recompute every 30s
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => clearInterval(id);
  }, []);
  const isCanceled = orderData?.status === 'canceled';
  const currentStepIndex = useMemo(() => orderData ? getCurrentStepIndex(orderData, now) : 0, [orderData, now]);
  const etaLabel = useMemo(() => orderData ? getEtaLabel(orderData, now) : '', [orderData, now]);
  const handleCancel = () => {
    if (!orderData) return;
    if (typeof window !== 'undefined' && !window.confirm(`Cancel order ${orderData.orderId}? This can't be undone.`)) {
      return;
    }
    const updated = cancelOrder(orderData.orderId);
    // Fall back to an in-place status flip if the order isn't persisted on
    // this device (e.g. it was opened purely from a share link token).
    setOrderData(updated || {
      ...orderData,
      status: 'canceled',
      canceledAt: Date.now()
    });
  };
  const handleShare = async () => {
    if (!orderData) return;
    const url = buildShareLink(orderData);
    try {
      if (navigator.share) {
        await navigator.share({
          title: `206Gravel Order ${orderData.orderId}`,
          text: 'Track my gravel delivery',
          url
        });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      }
    } catch {
      /* user cancelled */
    }
  };
  if (!orderData) {
    return <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4 bg-[#fdfbf7] dark:bg-[#1b1b1b]" data-id="element-3492" data-mp="293epjxdejcsj">
        <h1 className="text-2xl font-bold text-brand-charcoal dark:text-white mb-4" data-id="element-3493" data-mp="d11nvbx7k00v">
          Order Not Found
        </h1>
        <p className="text-stone-700 dark:text-stone-300 mb-2 max-w-md" data-id="element-3494" data-mp="hya61cicw424">
          We couldn't find tracking details for order {orderId} on this device.
        </p>
        <p className="text-stone-700 dark:text-stone-300 mb-6 max-w-md text-sm" data-id="element-3495" data-mp="88icmllztgz8">
          If you have a tracking link from your order email or text, open that
          link directly. Otherwise, call dispatch and we'll look it up.
        </p>
        <div className="flex gap-3" data-id="element-3496" data-mp="3uedrd0oqtzwg">
          <Link to="/track" className="px-5 py-2.5 rounded-lg border border-stone-200 dark:border-[#444] text-stone-700 dark:text-stone-300 font-medium hover:bg-stone-50 dark:hover:bg-[#222] transition-colors" data-id="element-3497" data-mp="3gh8plcw2bn70">
            Try another ID
          </Link>
          <a href="tel:206-619-3742" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-brand-orange text-white font-bold hover:bg-brand-orange/90 transition-colors" data-id="element-3498" data-mp="1eq5hovdk4mq5">
            <Phone className="w-4 h-4" data-id="element-3499" data-mp="346ziwdaiz4ex" />
            Call Dispatch
          </a>
        </div>
      </div>;
  }
  const totalSubtotal = orderData.items.reduce((sum, it) => sum + it.quantity * it.pricePerYard, 0);
  return <main className="py-12 md:py-20 bg-[#fdfbf7] dark:bg-[#1b1b1b] min-h-[calc(100vh-64px)]" data-id="element-3500" data-mp="3dxlwy32lfmvi">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3501" data-mp="3949k0q7a14il">
        <Link to="/track" className="inline-flex items-center text-sm font-medium text-brand-muted hover:text-brand-orange transition-colors mb-8" data-id="element-3502" data-mp="3mw64c82pea4f">
          <ArrowLeft className="w-4 h-4 mr-2" data-id="element-3503" data-mp="3hzz2aqdfhirr" />
          All orders
        </Link>

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4" data-id="element-3504" data-mp="ozj3lf4i5ce">
          <div data-id="element-3505" data-mp="3rtzbb3btz85r">
            <h1 className="font-heading text-3xl font-bold text-brand-charcoal dark:text-white" data-id="element-3506" data-mp="22h47wo1bsmiw">
              Track Order
            </h1>
            <p className="text-stone-700 dark:text-stone-300 mt-1" data-id="element-3507" data-mp="1xj2qs8ngmxa2">
              Order #{orderData.orderId}
            </p>
          </div>
          <div className="flex items-center gap-2" data-id="element-3508" data-mp="2n89ijdycp675">
            {isCanceled ? <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-[#DD4132]/10 border border-[#DD4132]/25 text-[#DD4132] text-sm font-bold" data-id="element-3509" data-mp="2r8jmkl2l1mjd">
                <XCircle className="w-4 h-4 mr-1.5" data-id="element-3510" data-mp="315obgsntbw3v" />
                Canceled
              </div> : currentStepIndex < 4 && <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-brand-orange/10 border border-brand-orange/20 text-brand-orange text-sm font-bold" data-id="element-3511" data-mp="365kf2qsllliw">
                  <span className="w-2 h-2 rounded-full bg-brand-orange animate-pulse mr-2" data-id="element-3512" data-mp="3a49pcaicamyb" />
                  {etaLabel}
                </div>}
            <button type="button" onClick={handleShare} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-stone-100 dark:bg-[#2a2a2a] border border-stone-200 dark:border-[#3a3a3a] text-stone-700 dark:text-stone-300 text-sm font-medium hover:bg-stone-200 dark:hover:bg-[#333] transition-colors" data-id="element-3513" data-mp="3jwp9neh3znmd">
              {copied ? <>
                  <Check className="w-4 h-4 text-[#04A05C]" data-id="element-3514" data-mp="1i56y2f8kojb6" />
                  Copied
                </> : <>
                  <Share2 className="w-4 h-4" data-id="element-3515" data-mp="1dajf0vwc8h3z" />
                  Share
                </>}
            </button>
          </div>
        </div>

        {isCanceled && <div className="mb-8 rounded-2xl border border-[#DD4132]/30 bg-[#DD4132]/[0.06] dark:bg-[#DD4132]/[0.08] p-5 flex items-start gap-3" data-id="element-3516" data-mp="13baqwjo5lwpj">
            <XCircle className="w-6 h-6 text-[#DD4132] shrink-0 mt-0.5" data-id="element-3517" data-mp="20zm06dq6ks38" />
            <div data-id="element-3518" data-mp="1vzgc17j4dnpw">
              <p className="font-bold text-brand-charcoal dark:text-white" data-id="element-3519" data-mp="1w10w237t5o9i">
                This order was canceled
              </p>
              <p className="text-sm text-stone-700 dark:text-stone-300 mt-1" data-id="element-3520" data-mp="1m9el87zdtr7a">
                Order #{orderData.orderId} was canceled at the customer's
                request
                {orderData.canceledAt ? ` on ${new Date(orderData.canceledAt).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })}` : ''}
                . No delivery is scheduled and no charge will be made. If you
                have any future gravel needs, we're here to serve them.
              </p>
            </div>
          </div>}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-3521" data-mp="1j7un34z0cp2v">
          {/* Left Col: Map & Summary */}
          <div className="md:col-span-2 space-y-6" data-id="element-3522" data-mp="26xozjlff0bml">
            {/* Mock Map — hidden once the order is canceled */}
            {!isCanceled && <div className="bg-white dark:bg-[#222] rounded-2xl border border-stone-200 dark:border-[#333] overflow-hidden shadow-sm h-64 relative" data-id="element-3523" data-mp="2az0hd4s1rsk8">
                <div className="absolute inset-0 bg-gradient-to-br from-stone-100 to-stone-200 dark:from-[#2a2a2a] dark:to-[#1a1a1a]" data-id="element-3524" data-mp="21ycbixct4yif" />
                <svg className="absolute inset-0 w-full h-full opacity-30 dark:opacity-20" preserveAspectRatio="none" viewBox="0 0 100 100" data-id="element-3525" data-mp="lju6e09bnkb4">
                  <path d="M10,90 Q30,50 50,50 T90,10" fill="none" stroke="#04A05C" strokeWidth="2" strokeDasharray="4 4" data-id="element-3526" data-mp="j0608tn0qg4l" />
                </svg>
                <div className="absolute bottom-4 left-4 w-4 h-4 bg-brand-charcoal dark:bg-white rounded-full border-2 border-white dark:border-black shadow-md" data-id="element-3527" data-mp="e01k54q0vnew" />
                <div className="absolute top-4 right-4 w-4 h-4 bg-brand-orange rounded-full border-2 border-white dark:border-black shadow-md" data-id="element-3528" data-mp="n20zztpawlg9" />

                {currentStepIndex >= 2 && currentStepIndex < 4 && <motion.div className="absolute z-10 bg-white dark:bg-black p-2 rounded-full shadow-lg border border-stone-200 dark:border-[#444]" initial={{
              left: '10%',
              top: '80%'
            }} animate={{
              left: currentStepIndex === 3 ? '50%' : '20%',
              top: currentStepIndex === 3 ? '50%' : '70%'
            }} transition={{
              duration: 2,
              ease: 'easeInOut'
            }} data-id="element-3529" data-mp="3rfzdmzm1zbhf">
                    <Truck className="w-5 h-5 text-[#04A05C]" data-id="element-3530" data-mp="37rqci02j7v5s" />
                  </motion.div>}

                <div className="absolute top-4 left-4 bg-white/90 dark:bg-black/80 backdrop-blur-sm px-4 py-2 rounded-lg border border-stone-200 dark:border-[#444] shadow-sm" data-id="element-3531" data-mp="2k9mjxkcs4qkl">
                  <p className="text-xs font-bold text-stone-500 dark:text-stone-400 uppercase tracking-wider mb-0.5" data-id="element-3532" data-mp="2aaxbsjaoqkhj">
                    Live ETA
                  </p>
                  <p className="text-lg font-bold text-brand-charcoal dark:text-white" data-id="element-3533" data-mp="21b0ho25j0tf3">
                    {currentStepIndex === 4 ? 'Delivered' : etaLabel}
                  </p>
                </div>
              </div>}

            {/* Order Summary */}
            <div className="bg-white dark:bg-[#222] rounded-2xl border border-stone-200 dark:border-[#333] p-6 shadow-sm" data-id="element-3534" data-mp="1rlqnmgr4h0oc">
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-4 flex items-center" data-id="element-3535" data-mp="1mkjgmjwt05iq">
                <FileText className="w-5 h-5 mr-2 text-brand-orange" data-id="element-3536" data-mp="1imlphbjh39cp" />
                Order Details
              </h3>

              <div className="space-y-4" data-id="element-3537" data-mp="3p9b7vrgkryh1">
                <div className="flex items-start gap-3" data-id="element-3538" data-mp="3p8jdgjyps6sc">
                  <MapPin className="w-5 h-5 text-stone-400 shrink-0 mt-0.5" data-id="element-3539" data-mp="3u7mkg9y98n2y" />
                  <div data-id="element-3540" data-mp="24bxm466dozh">
                    <p className="text-sm font-medium text-stone-900 dark:text-white" data-id="element-3541" data-mp="23tuqop0ccil">
                      {orderData.address}
                    </p>
                    <p className="text-xs text-stone-500 mt-0.5" data-id="element-3542" data-mp="71c3adz44pq2">
                      Instructions: {orderData.instructions || 'None'}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3" data-id="element-3543" data-mp="3ai2auxacfatv">
                  <Clock className="w-5 h-5 text-stone-400 shrink-0 mt-0.5" data-id="element-3544" data-mp="3fi6ugw6jslob" />
                  <div data-id="element-3545" data-mp="3kahyaa4esrm5">
                    <p className="text-sm font-medium text-stone-900 dark:text-white" data-id="element-3546" data-mp="3kaqadzspjswa">
                      {orderData.date ? (() => {
                      // Parse YYYY-MM-DD as local time (not UTC) so the
                      // displayed weekday doesn't shift back a day in
                      // Pacific time.
                      const m = orderData.date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
                      const d = m ? new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10)) : new Date(orderData.date);
                      return d.toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'short',
                        day: 'numeric'
                      });
                    })() : 'Date TBD'}
                    </p>
                    <p className="text-xs text-stone-500 mt-0.5" data-id="element-3547" data-mp="3786upf7clj6o">
                      Window: {orderData.time || 'Any time'}
                    </p>
                  </div>
                </div>

                <div className="border-t border-stone-100 dark:border-[#333] pt-4 mt-4" data-id="element-3548" data-mp="3g6irjyyt5d7k">
                  {orderData.items.map((item, i) => <div key={i} className="flex justify-between text-sm mb-2" data-id="element-3549" data-mp="10i91np3eki2j" data-mpi={i}>
                      <span className="text-stone-700 dark:text-stone-300" data-id="element-3550" data-mp="vgjni0kvvoa8">
                        {item.quantity} yds {item.product}
                      </span>
                      <span className="font-medium text-stone-900 dark:text-white" data-id="element-3551" data-mp="17zxify95n7wd">
                        ${(item.quantity * item.pricePerYard).toFixed(2)}
                      </span>
                    </div>)}
                  <div className="flex justify-between text-sm mb-2 text-stone-500" data-id="element-3552" data-mp="z01zv8d9samb">
                    <span data-id="element-3553" data-mp="1mobhrz7d4e86">Delivery Fee</span>
                    <span data-id="element-3554" data-mp="1mpd3qypx2l4o">${orderData.totalDeliveryFee.toFixed(2)}</span>
                  </div>
                  {orderData.saturdaySurcharge && orderData.saturdaySurcharge > 0 ? <div className="flex justify-between text-sm mb-2 text-stone-500" data-id="element-3555" data-mp="1cxqmolqwdru0">
                      <span data-id="element-3556" data-mp="1hxbor51d6gay">Saturday Delivery</span>
                      <span data-id="element-3557" data-mp="1hvs5r6obpr5h">${orderData.saturdaySurcharge.toFixed(2)}</span>
                    </div> : null}
                  <div className="flex justify-between text-sm mb-2 text-stone-500" data-id="element-3558" data-mp="25lmgnip315n3">
                    <span data-id="element-3559" data-mp="2afgyegl4najj">Tax</span>
                    <span data-id="element-3560" data-mp="ls37580kl1hg">${orderData.estimatedTax.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg mt-4 pt-4 border-t border-stone-100 dark:border-[#333] text-brand-charcoal dark:text-white" data-id="element-3561" data-mp="hqrdmrekrw3z">
                    <span data-id="element-3562" data-mp="crwutpgly7jv">Total</span>
                    <span data-id="element-3563" data-mp="7stqy8cd67dh">${orderData.finalTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Col: Timeline */}
          <div className="bg-white dark:bg-[#222] rounded-2xl border border-stone-200 dark:border-[#333] p-6 shadow-sm h-fit" data-id="element-3564" data-mp="10hhiglnphszh">
            <h3 className="font-bold text-brand-charcoal dark:text-white mb-6" data-id="element-3565" data-mp="vphaq71e9vsc">
              Status
            </h3>

            {isCanceled ? <div className="flex items-start gap-4" data-id="element-3566" data-mp="qr62g0qqztc8">
                <div className="w-8 h-8 rounded-full flex items-center justify-center bg-[#DD4132]/10 border-2 border-[#DD4132] text-[#DD4132] shrink-0" data-id="element-3567" data-mp="1o73bssheddzk">
                  <XCircle className="w-4 h-4" data-id="element-3568" data-mp="1jeaa6dh86lrp" />
                </div>
                <div className="pt-1" data-id="element-3569" data-mp="1qssxywb89pkj">
                  <p className="text-sm font-bold text-brand-charcoal dark:text-white" data-id="element-3570" data-mp="1vqcpgxwxryuf">
                    Order Canceled
                  </p>
                  <p className="text-xs mt-0.5 text-stone-500 dark:text-stone-400" data-id="element-3571" data-mp="20omjv6au6vez">
                    Canceled at the customer's request. No delivery scheduled.
                  </p>
                </div>
              </div> : <div className="relative" data-id="element-3572" data-mp="29pkonqt3n6ym">
                <div className="absolute left-4 top-4 bottom-4 w-0.5 bg-stone-100 dark:bg-[#333]" data-id="element-3573" data-mp="2ep72a1cuap7y" />

                <div className="space-y-8 relative" data-id="element-3574" data-mp="2jfxbjpodzc7x">
                  {STEPS.map((step, index) => {
                const isPast = index < currentStepIndex;
                const isCurrent = index === currentStepIndex;
                const Icon = step.icon;
                return <div key={step.id} className="flex items-start gap-4" data-id="element-3575" data-mp="2tfxuhaicfqbl" data-mpi={index}>
                        <div className="relative z-10 bg-white dark:bg-[#222] py-1" data-id="element-3576" data-mp="2xghtkjmh940d">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors duration-500 ${isPast ? 'bg-[#04A05C] border-[#04A05C] text-white' : isCurrent ? 'bg-white dark:bg-[#222] border-[#04A05C] text-[#04A05C]' : 'bg-white dark:bg-[#222] border-stone-200 dark:border-[#444] text-stone-300 dark:text-[#555]'}`} data-id="element-3577" data-mp="ybzv3ztj22bx">
                            <Icon className="w-4 h-4" data-id="element-3578" data-mp="1h1fuukxe1qra" />
                          </div>
                          {isCurrent && <span className="absolute inset-0 rounded-full border-2 border-[#04A05C] animate-ping opacity-20" style={{
                      top: '4px',
                      bottom: '4px'
                    }} data-id="element-3579" data-mp="1c2bl9t6jxr0f" />}
                        </div>

                        <div className="pt-1.5" data-id="element-3580" data-mp="1m10yv9rf0pav">
                          <p className={`text-sm font-bold transition-colors duration-500 ${isPast || isCurrent ? 'text-stone-900 dark:text-white' : 'text-stone-400 dark:text-[#666]'}`} data-id="element-3581" data-mp="3rsxv2lr9z6l5">
                            {step.label}
                          </p>
                          <p className={`text-xs mt-0.5 transition-colors duration-500 ${isPast || isCurrent ? 'text-stone-500 dark:text-stone-400' : 'text-stone-300 dark:text-[#555]'}`} data-id="element-3582" data-mp="3doqr5wfnlngi">
                            {step.desc}
                          </p>
                        </div>
                      </div>;
              })}
                </div>
              </div>}

            <div className="mt-8 pt-6 border-t border-stone-100 dark:border-[#333]" data-id="element-3583" data-mp="2tywzrjgupact">
              <p className="text-xs text-stone-500 mb-3" data-id="element-3584" data-mp="2yzb3wqfc005k">
                Need a real-time update?
              </p>
              <a href="tel:206-619-3742" className="flex items-center justify-center w-full py-2.5 rounded-lg border border-stone-200 dark:border-[#444] text-sm font-medium text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-[#2a2a2a] transition-colors" data-id="element-3585" data-mp="2l0l7ld0c64i9">
                <Phone className="w-4 h-4 mr-2" data-id="element-3586" data-mp="o7jev5c4yj7l" />
                Call Dispatch
              </a>
              {!isCanceled && <button type="button" onClick={handleCancel} className="mt-3 flex items-center justify-center w-full py-2.5 rounded-lg border border-[#DD4132]/30 text-sm font-medium text-[#DD4132] hover:bg-[#DD4132]/[0.06] transition-colors" data-id="element-3587" data-mp="1wqdu2nfdtumh">
                  <XCircle className="w-4 h-4 mr-2" data-id="element-3588" data-mp="1mzj53bar4gxc" />
                  Cancel order
                </button>}
            </div>
          </div>
        </div>
      </div>
    </main>;
}